import { useState } from "react";
import { PageHeader } from "@/components/data-display/page-header";
import { LoadingTable, NotAvailableState, PermissionDeniedState } from "@/components/ui/system-state";
import { SupportDetails } from "@/components/ui/copyable-reference";
import { Button } from "@/components/ui/button";
import { WipHint } from "@/components/ui/wip-hint";
import { useInvoicingSettings } from "@/hooks/use-invoices";
import { isDeniedFailure } from "@/lib/api-failure";
import type { IssueInvoiceResponse } from "@/features/invoicing/types";
import { COPY } from "@/features/invoicing/create/copy";
import { InvoiceForm } from "@/features/invoicing/create/invoice-form";
import { PayLinkReveal } from "@/features/invoicing/create/pay-link-reveal";
import { useInvoiceScopes } from "@/features/invoicing/create/use-invoice-scopes";
import { usePayers } from "@/features/invoicing/create/use-payers";

/**
 * `/stablecoins/invoices/new` — compose and issue an invoice (page spec
 * `invoice-create`).
 *
 * ## The four answers this page can give, and no fifth
 *
 *  - **loading** — the settings read and the payer read are both in flight. A
 *    `LoadingTable`, never a form with empty selects behind it.
 *  - **denied** — the service refused a read with a 403-shaped failure. A
 *    `PermissionDeniedState` carrying the capability the body named. Nothing is
 *    rendered that implies live configuration.
 *  - **error** — a read failed any other way. `NotAvailableState` with the
 *    reason code, plus `SupportDetails` with the support reference and
 *    capability. Again: no asset, no network, no payer, because none was
 *    returned. A form built from defaults here would show the operator
 *    collection wallets their organisation does not have.
 *  - **ready** — both reads answered, so `InvoiceForm` is composed from the
 *    settings response itself (`collectionWallets`, `networkLimits`) and the
 *    payer list. Once `POST /v1/invoices/{id}/issue` succeeds, the form is
 *    replaced by the one-time `PayLinkReveal`.
 *
 * ## What is never on this page
 *
 * No approve control and no reject control, for any principal. Approving a
 * resolution or a refund is the separate four-eyes surface
 * (`/stablecoins/invoices/resolutions`), where the approver must differ from
 * the proposer and a machine principal is refused outright. The create page
 * says that in prose rather than rendering an affordance it cannot support.
 *
 * The `<WipHint />` chip stays: the issuer contract has no backend in this
 * environment yet, and a page that renders real states must still say so.
 */
export default function InvoiceCreatePage() {
  const settings = useInvoicingSettings();
  const payers = usePayers();
  const scopes = useInvoiceScopes();
  const [issued, setIssued] = useState<IssueInvoiceResponse | null>(null);

  const loading = settings.loading || payers.loading;
  // The settings read is the one that can make the page meaningless, so its
  // failure is reported first; the payer read's failure is reported otherwise.
  const error = settings.error ?? payers.error;

  const refresh = () => {
    settings.refresh();
    payers.refresh();
  };

  return (
    <div>
      <PageHeader title={COPY.title} subtitle={COPY.subtitle} actions={<WipHint />} />

      {loading && <LoadingTable rows={6} cols={3} label={COPY.loading} />}

      {!loading && error && (
        <div className="flex flex-col items-center gap-2">
          {isDeniedFailure(error) ? (
            <PermissionDeniedState
              title={COPY.readDeniedTitle}
              description={COPY.readDeniedDescription}
              details={error.capability ? [{ k: "Needs", v: error.capability }] : undefined}
              action={
                <Button variant="secondary" onClick={refresh}>
                  Try again
                </Button>
              }
            />
          ) : (
            <NotAvailableState
              title={COPY.readErrorTitle}
              description={COPY.readErrorDescription}
              reason={error.reasonCode}
            />
          )}
          <SupportDetails reference={error.supportReference} capability={error.capability} className="justify-center" />
          {!isDeniedFailure(error) && (
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          )}
        </div>
      )}

      {!loading && !error && issued && <PayLinkReveal invoice={issued} />}

      {!loading && !error && !issued && settings.data && (
        <InvoiceForm settings={settings.data} payers={payers} scopes={scopes} onIssued={setIssued} />
      )}
    </div>
  );
}
